import type { DimLabelPlacementType } from "building-types/annotation/annotationTypes";
import type { PlacementXAnchor } from "building-types/annotation/annotationTypesEnum";
import type { BuildingEntityBase } from "building-types/entities/buildingEntityBase";
import type { AutoDimensionRuntimeHandle } from "building-types/handles/docEntityHandleTypes";
import type { EdgeData } from "building-types/provenance/halfEdgeDataTypes";
import type { DimensionPrecision } from "building-types/views/viewTypes";
import type { Multimap } from "common/data/multimap";
import type { AABB2 } from "geom/aabb2Types";
import type { PolyLine2 } from "geom/polyLine2Types";
import type { Vec2 } from "geom/vec2";
import type { DimensionRowLevel } from "./dimensionTypesEnum";

export interface DimensionGeometryProps {
  readonly pl: PolyLine2;
  readonly baseLine?: PolyLine2;
  readonly dimExt?: number;
  readonly labelPosX?: number;
  readonly labelPosY?: number;
  readonly rangeAABB?: AABB2;
  readonly halfEdges?: EdgeData[];
  readonly baseLineOffset?: number;
  readonly startExt?: number;
  readonly endExt?: number;
  readonly dimStrip?: string;
}

/**
 * Result of Building Generation
 */

export interface DimensionRowOutputNewProps extends DimensionGeometryProps {
  readonly rowLevel: DimensionRowLevel;
  readonly witnessLineGap: number;
}

export interface DimensionRowOutput extends DimensionRowOutputNewProps {
  readonly dimLine: PolyLine2;
}

export interface DimensionBase {
  // The main dimension line.
  readonly baseDimLine: PolyLine2;
  readonly dimLine: PolyLine2;
  // All of the polylines needs to render this dimension including the dimLine.
  readonly linework: PolyLine2[];
  readonly ticks: PolyLine2[];
  readonly witnessPls: PolyLine2[];
  // The label for the dim and the midpt.
  readonly label: string;
  readonly labelUnstacked: string;

  readonly startAnchor: Vec2;
  readonly endAnchor: Vec2;

  // styling properties
  readonly endExt?: number;
  readonly startExt?: number;
  readonly witnessLineGap?: number;

  labelMidPtX: number;
  labelMidPtY: number;
}

export interface Dimension extends DimensionBase {
  readonly labelPlacementType: DimLabelPlacementType;

  /** The text direction is set to the dimension line by default */
  readonly textDirXOverride?: number;
  readonly textDirYOverride?: number;

  readonly boxVerticalAlignmentPercentageOffset?: number;
  readonly placementXAnchor?: PlacementXAnchor;

  // used for relaxation
  readonly labelPerimeter?: PolyLine2;
  readonly labelAABB?: AABB2;
  readonly rangeAABB?: AABB2;
  readonly autoKind?: GeneratedDimensionPlacementType;
  readonly dimStrip?: string;

  // these are reserved generally for auto dimensions
  readonly halfEdges?: EdgeData[];
  readonly handle?: AutoDimensionRuntimeHandle;
  readonly rowLevel?: DimensionRowLevel;

  readonly precision?: DimensionPrecision;
}

export enum GeneratedDimensionPlacementType {
  "INTERIOR" = "INTERIOR",
  "EXTERIOR" = "EXTERIOR",
  "FOUNDATION_INTERIOR" = "FOUNDATION_INTERIOR",
  "FOUNDATION_EXTERIOR" = "FOUNDATION_EXTERIOR",
  "UTILITY_INTERIOR" = "UTILITY_INTERIOR",
  "UTILITY_EXTERIOR" = "UTILITY_EXTERIOR",
}

type GeneratedInteriorDimension = DimensionGeometryProps &
  BuildingEntityBase<"DIMENSION"> & { placement: GeneratedDimensionPlacementType.INTERIOR };

type GeneratedExteriorDimension = DimensionRowOutput &
  BuildingEntityBase<"DIMENSION"> & { placement: GeneratedDimensionPlacementType.EXTERIOR };

type GeneratedExteriorFoundationDimension = DimensionRowOutput &
  BuildingEntityBase<"DIMENSION"> & { placement: GeneratedDimensionPlacementType.FOUNDATION_EXTERIOR };

type GeneratedInteriorFoundationDimension = DimensionRowOutput &
  BuildingEntityBase<"DIMENSION"> & { placement: GeneratedDimensionPlacementType.FOUNDATION_INTERIOR };

type GeneratedInteriorUtilityDimension = DimensionRowOutput &
  BuildingEntityBase<"DIMENSION"> & { placement: GeneratedDimensionPlacementType.UTILITY_INTERIOR };

type GeneratedExteriorUtilityDimension = DimensionRowOutput &
  BuildingEntityBase<"DIMENSION"> & { placement: GeneratedDimensionPlacementType.UTILITY_EXTERIOR };

export type GeneratedDimension =
  | GeneratedInteriorDimension
  | GeneratedExteriorDimension
  | GeneratedExteriorFoundationDimension
  | GeneratedInteriorFoundationDimension
  | GeneratedInteriorUtilityDimension
  | GeneratedExteriorUtilityDimension;

export function isGeneratedInteriorDimension(dimension: GeneratedDimension): dimension is GeneratedInteriorDimension {
  return dimension.placement === GeneratedDimensionPlacementType.INTERIOR;
}

export function isGeneratedExteriorDimension(dimension: GeneratedDimension): dimension is GeneratedExteriorDimension {
  return dimension.placement === GeneratedDimensionPlacementType.EXTERIOR;
}

export function isGeneratedFoundationExteriorDimension(
  dimension: GeneratedDimension,
): dimension is GeneratedExteriorFoundationDimension {
  return dimension.placement === GeneratedDimensionPlacementType.FOUNDATION_EXTERIOR;
}

export function isGeneratedFoundationInteriorDimension(
  dimension: GeneratedDimension,
): dimension is GeneratedExteriorFoundationDimension {
  return dimension.placement === GeneratedDimensionPlacementType.FOUNDATION_INTERIOR;
}

export function isGeneratedUtilityInteriorDimension(
  dimension: GeneratedDimension,
): dimension is GeneratedInteriorUtilityDimension {
  return dimension.placement === GeneratedDimensionPlacementType.UTILITY_INTERIOR;
}

export function isGeneratedUtilityExteriorDimension(
  dimension: GeneratedDimension,
): dimension is GeneratedExteriorUtilityDimension {
  return dimension.placement === GeneratedDimensionPlacementType.UTILITY_EXTERIOR;
}

export type GeneratedDimensionMap = Multimap<GeneratedDimension>;
